'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { Check, X, CheckCircle2, Loader2, AlertCircle, Crown, Shield, Zap } from 'lucide-react';
import Header from '@/components/landing/Header';
import Footer from '@/components/landing/Footer';
import CTASection from '@/components/landing/CTASection';
import FAQSection from '@/components/landing/FAQSection';
import { useLanguage } from '@/contexts/LanguageContext';
import BrandSafeText from '@/components/landing/BrandSafeText';
import RichText from '@/components/landing/RichText';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || '';

/* Keys only — the API and the register-school link expect these strings. The
   visible label and suffix come from `pricing.cycles`, shared with /pricing so
   the two pages can't name the same billing cycle differently. */
const BILLING_CYCLES = ['monthly', 'threeMonths', 'sixMonths', 'yearly'];

/* Icons for the trust row, index-aligned with `plans.trust`. */
const TRUST_ICONS = [Shield, Zap, CheckCircle2];

/**
 * The page's single <h1> and its supporting copy. Shared by the loading
 * branch and the loaded view so the heading is present in the
 * server-rendered HTML regardless of whether the plans fetch has resolved.
 */
function PlansHeading() {
    const { t } = useLanguage();
    return (
        <>
            <h1 className="font-sora font-black text-4xl md:text-[56px] text-[#0d1117] leading-tight tracking-tight mb-6">
                {t('plans.headingBefore')}{' '}
                <span className="text-gradient-blue">{t('plans.headingAccent')}</span>
                {t('plans.headingAfter') && <>{' '}{t('plans.headingAfter')}</>}
            </h1>

            <p className="text-[17px] text-[#596875] max-w-[600px] mx-auto leading-relaxed font-medium">
                {t('plans.subtitle')}
            </p>
        </>
    );
}

export default function PlansPage() {
    const { t, tv, tf } = useLanguage();
    const cycleCopy = tv('pricing.cycles');
    const trustLabels = tv('plans.trust');

    /* Rebuilt per render so it follows the language — FAQSection renders a
       caller's categories verbatim and never translates them itself. */
    const plansFaq = [
        { id: 'plans', label: t('plans.faqLabel'), icon: Crown, items: tv('plans.faqItems') },
    ];

    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/subscriptions/plans`);
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                const data = await response.json();
                if (data.success) {
                    setPlans(data.plans);
                } else {
                    setError(t('plans.errorLoad'));
                }
            } catch (err) {
                setError(t('plans.errorConnect'));
            } finally {
                setLoading(false);
            }
        };
        fetchPlans();
    }, []);

    const getPriceDisplay = (plan, cycle) => {
        const pricing = plan.pricing[cycle];
        if (!pricing) return { price: 0, salePrice: null, onSale: false };
        return { price: pricing.price, salePrice: pricing.salePrice, onSale: pricing.onSale };
    };

    if (loading) {
        return (
            <div className="landing-root min-h-screen bg-[#FAFCFF]">
                <Header />
                {/* The heading renders even while plans are still being
                    fetched — this branch is what the server sends, so
                    without it crawlers would only ever see a spinner. */}
                <div className="min-h-[calc(100vh-76px)] flex flex-col items-center justify-center gap-8 px-4 text-center">
                    <PlansHeading />
                    <Loader2 className="w-10 h-10 text-[#2040CF] animate-spin" />
                </div>
                <Footer />
            </div>
        );
    }

    if (error) {
        return (
            <div className="landing-root min-h-screen bg-[#FAFCFF]">
                <Header />
                <div className="min-h-[calc(100vh-76px)] flex flex-col items-center justify-center gap-4">
                    <AlertCircle className="w-12 h-12 text-red-400" />
                    <p className="text-[#596875] font-medium">{error}</p>
                    <button onClick={() => window.location.reload()}
                        className="px-6 py-2.5 bg-[#3D5EE1] text-white rounded-xl font-bold hover:bg-[#1E3BB3] transition-colors">
                        {t('plans.retry')}
                    </button>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="landing-root min-h-screen bg-[#FAFCFF]">
            <Header />
            <div className="py-24 relative overflow-hidden">
            {/* Background decorations */}
            <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-[#3D5EE1]/5 to-transparent -z-10" />
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-[#3D5EE1]/8 rounded-full blur-3xl -z-10" />
            <div className="absolute top-1/2 -right-24 w-80 h-80 bg-[#3D5EE1]/5 rounded-full blur-3xl -z-10" />

            <div className="site-container text-center">

                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    className="mb-16"
                >
                    <div className="inline-flex items-center gap-2 py-1.5 px-5 rounded-full bg-white border border-[#3D5EE1]/10 shadow-sm mb-6">
                        <Crown size={14} className="text-[#2040CF]" />
                        <span className="font-sora font-semibold text-[13px] text-[#1B35AC]">Flexible Plans for Every School</span>
                    </div>

                    <PlansHeading />
                </motion.div>

                {/* Billing Cycle Toggle */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="flex justify-center mb-16"
                >
                    <div className="p-1.5 bg-white border border-[#2A48C9]/15 rounded-[20px] shadow-[0_8px_32px_rgba(61,94,225,0.06)] inline-flex">
                        {BILLING_CYCLES.map((cycle, i) => (
                            <button key={cycle} onClick={() => setBillingCycle(cycle)}
                                className={`px-5 py-2.5 rounded-2xl text-[14px] font-sora font-bold transition-all duration-300 ${billingCycle === cycle
                                    ? 'bg-gradient-to-r from-[#3D5EE1] to-[#1E3BB3] text-white shadow-[0_4px_16px_rgba(61,94,225,0.4)]'
                                    : 'text-[#596875] hover:text-[#1E3BB3] hover:bg-[#2A48C9]/5'
                                    }`}>
                                {cycleCopy?.[i]?.label}
                            </button>
                        ))}
                    </div>
                </motion.div>

                {/* Plans Grid */}
                {plans.length === 0 ? (
                    <div className="text-center py-20">
                        <p className="text-[#94a3b8] font-medium">No active plans available at the moment.</p>
                    </div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.3 }}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch mb-24"
                    >
                        {plans.map((plan) => {
                            const { price, salePrice, onSale } = getPriceDisplay(plan, billingCycle);
                            const activePrice = onSale ? salePrice : price;
                            const isPopular = plan.isMostPopular;
                            const isRecommended = plan.isRecommended;
                            const included = (plan.keyPoints || []).map(text => ({ text, included: true }));
                            const excluded = (plan.excludedFeatures || []).map(text => ({ text, included: false }));
                            const allFeatures = [...included, ...excluded];

                            return (
                                <div key={plan._id}
                                    className={`group relative flex flex-col p-8 md:p-10 bg-white rounded-[32px] border-2 transition-all duration-500 hover:scale-[1.02] ${isPopular
                                        ? 'border-[#3D5EE1] shadow-[0_20px_60px_rgba(61,94,225,0.15)] scale-[1.02] z-10'
                                        : isRecommended
                                            ? 'border-[#3D5EE1]/60 shadow-[0_16px_48px_rgba(61,94,225,0.12)]'
                                            : 'border-[#E8F4FD] shadow-[0_8px_32px_rgba(61,94,225,0.05)] hover:border-[#3D5EE1]/30'
                                        }`}
                                >
                                    {isPopular && (
                                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-[#3D5EE1] to-[#1E3BB3] text-white text-[10px] font-black uppercase tracking-[0.2em] px-6 py-2 rounded-full shadow-[0_4px_16px_rgba(61,94,225,0.4)] z-20 whitespace-nowrap">
                                            {t('pricing.mostPopular')}
                                        </div>
                                    )}
                                    {isRecommended && !isPopular && (
                                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white text-[#1E3BB3] border-2 border-[#2A48C9] text-[10px] font-black uppercase tracking-[0.2em] px-6 py-2 rounded-full shadow-[0_4px_16px_rgba(61,94,225,0.18)] z-20 whitespace-nowrap">
                                            {t('pricing.recommended')}
                                        </div>
                                    )}

                                    <div className="text-left mb-6">
                                        <h3 translate="yes" className={`font-sora text-2xl font-black mb-2 ${isPopular ? 'text-[#3D5EE1]' : 'text-[#0d1117]'}`}>
                                            <BrandSafeText>{plan.name}</BrandSafeText>
                                        </h3>
                                        <p translate="yes" className="text-[#596875] text-[14px] leading-relaxed font-medium min-h-[40px]">
                                            <BrandSafeText>{plan.description}</BrandSafeText>
                                        </p>
                                    </div>

                                    <div className="mb-8 text-left">
                                        <div className="flex items-baseline gap-1.5">
                                            <span className="font-sora text-5xl font-black text-[#0d1117] tracking-tight">
                                                ₹{activePrice?.toLocaleString('en-IN') ?? '-'}
                                            </span>
                                            <span className="text-[#94a3b8] font-semibold text-lg">
                                                {cycleCopy?.[BILLING_CYCLES.indexOf(billingCycle)]?.suffix}
                                            </span>
                                        </div>
                                        {onSale && (
                                            <div className="flex items-center gap-2 mt-2">
                                                <span className="text-[#94a3b8] line-through font-semibold text-sm">₹{price?.toLocaleString('en-IN')}</span>
                                                <span className="text-[11px] font-black text-[#1E3BB3] bg-[#2A48C9]/10 px-2 py-0.5 rounded-full">
                                                    {tf('pricing.save', { percent: Math.round(((price - salePrice) / price) * 100) })}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <Link href={`/auth/register-school?plan=${plan._id}&billing=${billingCycle}`}
                                        className={`w-full py-4 px-8 rounded-2xl text-[15px] font-bold transition-all mb-8 text-center flex items-center justify-center gap-2 ${isPopular || isRecommended
                                            ? 'bg-gradient-to-r from-[#2040CF] to-[#1B35AC] text-white shadow-[0_8px_24px_rgba(61,94,225,0.3)] hover:shadow-[0_12px_32px_rgba(61,94,225,0.4)]'
                                            : 'bg-white text-[#2040CF] border-2 border-[#3D5EE1]/30 hover:border-[#3D5EE1] hover:bg-[#3D5EE1]/5'
                                            }`}>
                                        {t('pricing.choosePlan')}
                                        <CheckCircle2 className="w-5 h-5 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                                    </Link>

                                    {allFeatures.length > 0 && (
                                        <div className="text-left space-y-4 flex-grow">
                                            <p className="text-[11px] font-black text-[#94a3b8] uppercase tracking-widest mb-2">{t('pricing.whatsIncluded')}</p>
                                            {allFeatures.map((feature, idx) => (
                                                <div key={idx} className="flex items-center gap-3.5">
                                                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${feature.included ? 'bg-[#3D5EE1]/10 text-[#2040CF]' : 'bg-gray-100 text-gray-300'}`}>
                                                        {feature.included
                                                            ? <Check className="w-3.5 h-3.5" strokeWidth={3} />
                                                            : <X className="w-3.5 h-3.5" strokeWidth={3} />
                                                        }
                                                    </div>
                                                    <span translate="yes" className={`text-[14px] font-medium ${feature.included ? 'text-[#4A5568]' : 'text-[#94a3b8] line-through decoration-2 opacity-60'}`}>
                                                        <BrandSafeText>{feature.text}</BrandSafeText>
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </motion.div>
                )}

                {/* Trust Indicators */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="flex flex-wrap items-center justify-center gap-8 mb-20"
                >
                    {trustLabels.map((label, i) => {
                        const Icon = TRUST_ICONS[i];
                        return (
                        <div key={i} className="flex items-center gap-2 text-[#596875]">
                            <Icon size={16} className="text-[#3D5EE1]" />
                            <span className="text-[14px] font-semibold">{label}</span>
                        </div>
                        );
                    })}
                </motion.div>
            </div>
            </div>
            {/* Only the loaded branch gets the FAQ and CTA — the loading and
                error branches above stay bare so a spinner or a retry prompt
                isn't buried under full-height marketing bands. */}
            <FAQSection
                heading={<RichText text={t('plans.faqHeading')} />}
                categories={plansFaq}
            />
            <CTASection />
            <Footer />
        </div>
    );
}

